/**
 * Types for the ABO Credits tab. Mirrors, field for field, the
 * digital-services contract of `GET /v1/abo/customers/:id/credits` and
 * `POST /v1/abo/customers/:id/credits/adjustments` (abo-member-credits
 * TechSpec §API Endpoints 1 and 2).
 *
 * The balance and the history come from the DS credits ledger only (never
 * HubSpot). DS is the only writer of credits: ABO changes a balance through
 * the adjustment route alone.
 */

/** The only credit type the ledger holds today. */
export type CreditType = "print";

/** Why a ledger entry was written. */
export type CreditLedgerReason =
  | "purchase"
  | "subscription_credit_grant"
  | "book_print_debit"
  | "mod_order_debit"
  | "refund"
  | "adjustment"
  | "downgrade_clawback";

/** What a ledger entry refers to (`refId` is that object's id). */
export type CreditLedgerRefType =
  | "mod_order"
  | "book_order"
  | "chargebee_invoice"
  | "subscription_event"
  | "manual"
  | "operator_adjustment";

/** One ledger movement, newest first in the history. */
export interface AboCreditHistoryEntry {
  id: string;
  creditType: CreditType;
  /** Signed change. */
  delta: number;
  balanceAfter: number;
  /** Typed as an open string so a reason added in DS later still renders. */
  reason: CreditLedgerReason | (string & Record<never, never>);
  refType: CreditLedgerRefType | (string & Record<never, never>);
  refId: string;
  /** ISO-8601. */
  createdAt: string;
  /** Operator adjustments only: the verified operator e-mail. */
  actor?: string;
  /** Operator adjustments only: the reason the operator typed. */
  note?: string;
  /** Operator adjustments only; differs from `delta` when a removal clamped. */
  requestedDelta?: number;
}

/** Ledger paginator meta; `currentPage` is 0-based. */
export interface AboCreditHistoryMeta {
  total: number;
  perPage: number;
  currentPage: number;
  lastPage: number;
}

/** Response of `GET /v1/abo/customers/:id/credits`. */
export interface AboMemberCreditsResponse {
  customerId: string;
  creditType: CreditType;
  /** `ds__credit_balance`; 0 when the member has no balance document. */
  balance: number;
  /**
   * Whether the DS ledger is the member's live balance (ADR-003). While it is
   * false, ABO shows a notice and offers no adjustment.
   */
  ledgerAuthoritative: boolean;
  /** Largest |amount| the ABO adjustment route accepts. */
  adjustmentLimit: number;
  history: {
    data: AboCreditHistoryEntry[];
    meta: AboCreditHistoryMeta;
  };
}

/** Body of `POST /v1/abo/customers/:id/credits/adjustments`. */
export interface AboCreditAdjustmentRequest {
  /** Signed integer, never 0, at most `adjustmentLimit` in magnitude. */
  amount: number;
  /** Trimmed, 1..500 characters. */
  reason: string;
  /** `abo-<uuid v4>`: one per confirmation, reused on a retry. */
  idempotencyKey: string;
}

/** Response of `POST /v1/abo/customers/:id/credits/adjustments`. */
export interface AboCreditAdjustmentResponse {
  customerId: string;
  creditType: CreditType;
  /** Signed, as asked. */
  requested: number;
  /** Signed, as written; 0 when the balance was already 0. */
  applied: number;
  balanceAfter: number;
  /** `null` when nothing was written. */
  entryId: string | null;
  /** A removal reduced to the available balance. */
  clamped: boolean;
  /** Same key and payload as an earlier request: nothing new was written. */
  replayed: boolean;
  /** The stored key, `admin-adjustment:<key>`. */
  idempotencyKey: string;
  /** The verified operator e-mail. */
  actor: string;
}

/**
 * The history filter (`type` query parameter of the credits route): DS maps
 * each to ledger reasons (grants = purchase, subscription grant, refund;
 * spend = book print, MOD order, downgrade clawback; adjustments).
 */
export const CREDITS_HISTORY_TYPES = [
  "all",
  "grants",
  "spend",
  "adjustments",
] as const;

export type CreditsHistoryType = (typeof CREDITS_HISTORY_TYPES)[number];

/** Rows per history page (DS default and the PRD's page size). */
export const CREDITS_HISTORY_PAGE_SIZE = 10;

/** Longest reason DS accepts on an adjustment. */
export const CREDITS_ADJUST_REASON_MAX_LENGTH = 500;

/** The ABO route cap, used when an older answer carries no `adjustmentLimit`. */
export const CREDITS_ADJUST_DEFAULT_LIMIT = 100;

/** Prefix of every idempotency key ABO sends. */
export const CREDITS_IDEMPOTENCY_KEY_PREFIX = "abo-";

export const CREDITS_ADJUST_OPERATOR_UNVERIFIED =
  "CREDITS_ADJUST_OPERATOR_UNVERIFIED";
export const CREDITS_ADJUST_FORBIDDEN = "CREDITS_ADJUST_FORBIDDEN";
export const CREDITS_CUSTOMER_NOT_FOUND = "CUSTOMER_NOT_FOUND";
export const CREDITS_LEDGER_NOT_AUTHORITATIVE =
  "CREDITS_LEDGER_NOT_AUTHORITATIVE";
export const CREDITS_ADJUST_KEY_CONFLICT = "CREDITS_ADJUST_KEY_CONFLICT";
export const CREDITS_ADJUST_BALANCE_CHANGED = "CREDITS_ADJUST_BALANCE_CHANGED";
export const CREDITS_ADJUST_UNAVAILABLE = "CREDITS_ADJUST_UNAVAILABLE";

/**
 * The typed refusals of the adjustment (TechSpec refusal table), each with
 * the HTTP status DS answers it with. A code on any other status is not
 * recognised (same rule as the password reveal).
 */
export const CREDITS_ADJUST_REFUSAL_STATUS = {
  [CREDITS_ADJUST_OPERATOR_UNVERIFIED]: 401,
  [CREDITS_ADJUST_FORBIDDEN]: 403,
  [CREDITS_CUSTOMER_NOT_FOUND]: 404,
  [CREDITS_LEDGER_NOT_AUTHORITATIVE]: 409,
  [CREDITS_ADJUST_KEY_CONFLICT]: 409,
  [CREDITS_ADJUST_BALANCE_CHANGED]: 409,
  [CREDITS_ADJUST_UNAVAILABLE]: 503,
} as const;

export type CreditsAdjustRefusalCode =
  keyof typeof CREDITS_ADJUST_REFUSAL_STATUS;

/** A recognised adjustment refusal. */
export interface CreditsAdjustRefusal {
  code: CreditsAdjustRefusalCode;
}
